import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

interface CancelDeletionPageProps {
  onBack?: () => void;
}

export const CancelDeletionPage: React.FC<CancelDeletionPageProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState<any>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  
  const { login, cancelAccountDeletion, getAccountDeletionStatus } = useAuthStore();

  // Check deletion status on component mount
  useEffect(() => {
    const checkStatus = async () => {
      if (!email || !password) {
        setIsCheckingStatus(false);
        return;
      }

      try {
        // First login to get the token
        const loginResult = await login({ email, password });
        if (loginResult) {
          // Then check deletion status
          const status = await getAccountDeletionStatus();
          setDeletionStatus(status);
        }
      } catch (error) {
        console.error('Failed to check status:', error);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkStatus();
  }, [email, password, login, getAccountDeletionStatus]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }

    setIsLoading(true);
    try {
      const result = await login({ email, password });
      if (result) {
        // Check deletion status after successful login
        const status = await getAccountDeletionStatus();
        setDeletionStatus(status);
        toast.success('Logged in successfully');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setIsLoading(true);
    try {
      const result = await cancelAccountDeletion();
      if (result.success) {
        toast.success('Account deletion cancelled successfully!');
        setDeletionStatus({ isPendingDeletion: false });
      } else {
        toast.error(result.message || 'Failed to cancel deletion');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel deletion');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (dateString: string) => {
    const deletionDate = new Date(dateString);
    const now = new Date();
    const diffTime = deletionDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Header */}
          <div className="flex items-center mb-6">
            {onBack && (
              <button
                onClick={onBack}
                className="mr-3 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Cancel Account Deletion
            </h2>
          </div>

          {!deletionStatus ? (
            // Login form to access deletion status
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-200">
                    <p className="font-medium mb-1">Account Deletion in Progress</p>
                    <p>
                      Your account has been scheduled for deletion. Please log in to check your deletion status 
                      and cancel the deletion if desired.
                    </p>
                  </div>
                </div>
              </div>

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                isLoading={isLoading || isCheckingStatus}
              >
                {isLoading ? 'Checking Status...' : 'Check Deletion Status'}
              </Button>
            </form>
          ) : deletionStatus.isPendingDeletion ? (
            // Show deletion status and cancellation option
            <div className="space-y-6">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-medium text-red-800 dark:text-red-200 mb-2">
                      Account Scheduled for Deletion
                    </h3>
                    <div className="text-sm text-red-700 dark:text-red-300 space-y-1">
                      {deletionStatus.permanentDeletionDate && (
                        <>
                          <p>
                            <strong>Permanent deletion:</strong> {formatDate(deletionStatus.permanentDeletionDate)}
                          </p>
                          <p>
                            <strong>Days remaining:</strong> {getDaysRemaining(deletionStatus.permanentDeletionDate)} days
                          </p>
                        </>
                      )}
                      {deletionStatus.deletedAt && (
                        <p>
                          <strong>Deletion requested:</strong> {formatDate(deletionStatus.deletedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-2">You can still cancel the deletion</p>
                  <p>
                    If you cancel now, your account will be restored and you'll regain full access to all your data.
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={handleCancelDeletion}
                  variant="primary"
                  className="flex-1"
                  isLoading={isLoading}
                >
                  Cancel Deletion & Restore Account
                </Button>
                {onBack && (
                  <Button
                    onClick={onBack}
                    variant="outline"
                  >
                    Back
                  </Button>
                )}
              </div>
            </div>
          ) : (
            // Account is not pending deletion
            <div className="space-y-6">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-green-800 dark:text-green-200">
                    <p className="font-medium mb-1">Account is Active</p>
                    <p>Your account is not scheduled for deletion. You can continue using Koru normally.</p>
                  </div>
                </div>
              </div>

              {onBack && (
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="w-full"
                >
                  Back to Login
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
